import { jsPDF } from "jspdf";
let cachedLogo: HTMLImageElement | null = null;
export interface PdfCompanyInfo {
    companyName: string;
    address: string;
    phone: string;
    email: string;
}

export const COMPANY_INFO: PdfCompanyInfo = {
    companyName: "",
    address: "Calle 34 #8b-40 Oficina 202",
    phone: "(606) 326 67 45 - (+57) 313 850 7431",
    email: "talentohumano@contribuirempresarial.coop"
};

/**
 * Carga una imagen desde /public/assets
 */
export function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {

        const image = new Image();

        image.onload = () => resolve(image);

        image.onerror = reject;

        image.src = src;

    });
}

/**
 * Logo institucional
 */
export async function loadCompanyLogo(): Promise<HTMLImageElement> {

    if (cachedLogo) {
        return cachedLogo;
    }

    cachedLogo = await loadImage("/assets/images/LogoContribuir.png");

    return cachedLogo;

}

/**
 * Devuelve el ancho útil de la hoja.
 */
export function pageWidth(doc: jsPDF) {

    return doc.internal.pageSize.getWidth();

}

/**
 * Devuelve el alto útil de la hoja.
 */
export function pageHeight(doc: jsPDF) {

    return doc.internal.pageSize.getHeight();

}

/**
 * Centra un texto horizontalmente.
 */
export function centeredText(
    doc: jsPDF,
    text: string,
    y: number,
    fontSize = 11,
    bold = false
) {

    doc.setFontSize(fontSize);

    doc.setFont("helvetica", bold ? "bold" : "normal");

    doc.text(
        text,
        pageWidth(doc) / 2,
        y,
        {
            align: "center"
        }
    );

}

/**
 * Verifica si aún hay espacio en la página.
 * Si no lo hay crea una nueva página.
 */
export function ensureSpace(
    doc: jsPDF,
    currentY: number,
    requiredSpace: number
) {

    const footerSpace = 25;

    if (currentY + requiredSpace < pageHeight(doc) - footerSpace) {

        return currentY;

    }

    doc.addPage();

    return 20;

}

/**
 * Encabezado corporativo
 */
export async function drawHeader(
    doc: jsPDF,
    title: string
): Promise<number> {

    const logo = await loadCompanyLogo();
    const logoWidth = 60.5;
    const logoHeight = 27; // Manteniendo la proporción

    doc.addImage(
        logo,
        "PNG",
        pageWidth(doc) - logoWidth - 14,
        10,
        logoWidth,
        logoHeight
    );

    doc.setTextColor(110,110,110);
    doc.setFont("helvetica", "bold");

    doc.setFontSize(18);

    doc.text(
        COMPANY_INFO.companyName,
        48,
        18
    );

    doc.setTextColor(150,150,150);
    doc.setFontSize(13);

    doc.setFont("helvetica", "normal");

    doc.text(
        title,
        48,
        27
    );

    doc.setDrawColor(180);

    return 45;

}

/**
 * Pie de página institucional
 */
export function drawFooter(
    doc: jsPDF
) {

    const y = pageHeight(doc) - 10;

    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.4);

    doc.line(
        14,
        y - 6,
        pageWidth(doc) - 14,
        y - 6
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(110, 110, 110);

    centeredText(
        doc,
        `${COMPANY_INFO.phone}   |   ${COMPANY_INFO.address}   |   ${COMPANY_INFO.email}`,
        y,
        9
    );

    doc.setTextColor(0, 0, 0);

}

/**
 * Observaciones corporativas
 */
export function drawObservations(
    doc: jsPDF,
    startY: number
): number {

    const observations = [
        "• El servicio comprende únicamente las actividades contratadas con Contribuir Empresarial CTA.",
        "• Las cancelaciones deberán informarse con mínimo un (1) día hábil de anticipación; de lo contrario, el servicio programado será facturado.",
        "• Para la adecuada prestación del servicio, el cliente deberá garantizar el acceso al inmueble y la disponibilidad de agua y energía.",
        "• Cualquier modificación en la programación será informada oportunamente a través de nuestros canales oficiales."
    ];

    // Calcular cuánto espacio realmente ocupan
    let requiredHeight = 8; // título

    observations.forEach((line) => {
        const split = doc.splitTextToSize(line, 250);
        requiredHeight += split.length * 6;
    });

    // Ahora sí verificar espacio
    let y = ensureSpace(doc, startY, requiredHeight + 4);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);

    doc.text("Observaciones", 14, y);

    y += 8;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    observations.forEach((line) => {

        const split = doc.splitTextToSize(line, 250);

        doc.text(split, 18, y);

        y += split.length * 6;

    });

    return y + 4;
}

/**
 * Mensaje institucional
 */
export function drawThankYouMessage(
    doc: jsPDF,
    startY: number
): number {

    const message =
        "Agradecemos la confianza depositada en Contribuir Empresarial CTA. Nuestro compromiso es brindar un servicio responsable, oportuno y de calidad, orientado al bienestar de nuestros clientes y al desarrollo digno de nuestros asociados.";

    const split = doc.splitTextToSize(message, 220);

    const requiredHeight =
        (split.length * 6) + 4;

    let y = ensureSpace(
        doc,
        startY,
        requiredHeight
    );

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);

    doc.text(
        split,
        pageWidth(doc) / 2,
        y,
        {
            align: "center"
        }
    );

    return y + (split.length * 6);
}

/**
 * Dibuja la información del servicio
 */
export function drawSpotInformation(
    doc: jsPDF,
    cliente: string,
    semana: string,
    direccion?: string,
    estado = "Confirmado"
): number {

    let y = 50;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);

    doc.text("Cliente:", 14, y);

    doc.setFont("helvetica", "normal");

    doc.text(cliente, 36, y);

    y += 7;

    doc.setFont("helvetica", "bold");

    doc.text("Semana:", 14, y);

    doc.setFont("helvetica", "normal");

    doc.text(semana, 36, y);

    if (direccion) {

        y += 7;

        doc.setFont("helvetica", "bold");

        doc.text("Dirección del servicio:", 14, y);

        doc.setFont("helvetica", "normal");

        doc.text(direccion, 62, y);

    }

    y += 7;

    doc.setFont("helvetica", "bold");

    doc.text("Estado:", 14, y);

    doc.setFont("helvetica", "normal");

    doc.text(estado, 36, y);

    return y + 8;
}

/**
 * Dibuja header y footer en una página nueva.
 */
export async function createCorporatePage(
    doc: jsPDF,
    title: string
): Promise<number> {

    const y = await drawHeader(doc, title);

    drawFooter(doc);

    return y;

}